'use client';

import { useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowRight, ExternalLink, Loader2, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { reopenApplicationAction } from '@kit/auditions/server';
import type { ApplicationStructure } from '@kit/auditions/types';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { AnonymousApplyCta } from '~/_components/anonymous-apply-cta';
import {
  buildApplyJourneyNext,
  pendingAuditionIntent,
} from '~/_lib/apply-journey';
import { isSafeExternalHref } from '~/lib/safe-external-href';
import { ApplyDialog } from './apply-dialog';
import { AuditionExternalLink } from './audition-external-link';

interface SingleAuditionApplyCtaProps {
  auditionId: string;
  auditionTitle: string;
  slug: string;
  isOpen: boolean;
  isAuthenticated: boolean;
  externalLink?: string | null;
  applicationStructure: ApplicationStructure | null;
  prefillDefaults?: Record<string, unknown>;
  /** True when the user has a `profiles` row */
  hasProfile: boolean;
  /** True when the user has a `artist_profiles` row */
  hasDancerProfile: boolean;
  existingApplication: {
    id: string;
    status: string;
  } | null;
  /**
   * Whether the organizer permits entrants to edit after acceptance
   * (`auditions.allow_accepted_edits`, default true). When true an `accepted`
   * application surfaces an "Update your application" CTA into its editable
   * detail page; when false it stays status-only (no button).
   */
  allowAcceptedEdits?: boolean;
}

/**
 * Apply CTA for the single-audition (non-competition) flow rendered inside
 * `PublicAuditionView`'s right rail. The public `/auditions/[slug]` URL is
 * the single canonical surface — the apply form opens here as a dialog when
 * the user is authenticated.
 *
 * State table (top to bottom = priority). Mirrors Meteor's WAI
 * ApplicationManagerStatus edit-button table — clicking on an existing
 * application always routes to the application page; the page itself
 * decides edit / view / reopen mode based on status.
 *
 * 1. external_link → "Apply on external site"
 * 2. existing application → status-aware link to the application page
 *    (Continue / See / Reopen)
 * 3. status !== 'open' (and no existing app) → disabled "Applications closed"
 * 4. !isAuthenticated → "Sign in to apply"
 * 5. !hasProfile || !hasDancerProfile → "Complete profile to apply"
 * 6. fallback → "Apply now" (opens dialog)
 */
export function SingleAuditionApplyCta({
  auditionId,
  auditionTitle,
  slug,
  isOpen,
  isAuthenticated,
  externalLink,
  applicationStructure,
  prefillDefaults,
  hasProfile,
  hasDancerProfile,
  existingApplication,
  allowAcceptedEdits = true,
}: SingleAuditionApplyCtaProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isReopening, startReopen] = useTransition();

  if (isSafeExternalHref(externalLink)) {
    return (
      <Button asChild className="h-11 w-full">
        <AuditionExternalLink
          auditionId={auditionId}
          linkType="apply"
          href={externalLink}
        >
          <Trans i18nKey="auditions:singleApplyCta.applyExternal" />
          <ExternalLink className="ml-2 h-4 w-4" />
        </AuditionExternalLink>
      </Button>
    );
  }

  // Existing application → mirror Meteor's `applicationCanBeEdited`
  // (open|issues|submitted|archived) for which a CTA renders. `accepted`
  // and `rejected` show no button; the status pill above tells the dancer
  // the outcome and the My Applications list is the canonical re-entry
  // point. Archived: one-click reopen (status flips, then redirect) — no
  // 2-step "land then click again" detour.
  if (existingApplication) {
    const cta = ctaForExistingApplication(
      existingApplication.status,
      allowAcceptedEdits,
    );
    if (cta === null) return null;

    if (existingApplication.status === 'archived') {
      return (
        <Button
          className="h-11 w-full"
          variant={cta.variant}
          onClick={() => {
            startReopen(async () => {
              const result = await reopenApplicationAction(
                existingApplication.id,
              );
              if (result.success) {
                toast.success(t('auditions:apply.toastReopened'));
                router.push(
                  `/home/auditions/applications/${existingApplication.id}`,
                );
              } else {
                toast.error(
                  typeof result.error === 'string'
                    ? result.error
                    : t('auditions:applications.toast.reopenFailed'),
                );
              }
            });
          }}
          disabled={isReopening}
        >
          {isReopening ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            cta.icon
          )}
          {cta.label}
        </Button>
      );
    }

    return (
      <Button asChild className="h-11 w-full" variant={cta.variant}>
        <Link href={`/home/auditions/applications/${existingApplication.id}`}>
          {cta.icon}
          {cta.label}
        </Link>
      </Button>
    );
  }

  if (!isOpen) {
    return (
      <Button disabled className="h-11 w-full">
        <Trans i18nKey="auditions:singleApplyCta.applicationsClosed" />
      </Button>
    );
  }

  if (!isAuthenticated) {
    return (
      <AnonymousApplyCta
        target={{ surface: 'audition', slug }}
        auditionTitle={auditionTitle}
        ctaLabel={t('auditions:singleApplyCta.applyNow')}
      />
    );
  }

  if (!hasProfile || !hasDancerProfile) {
    const target = { surface: 'audition' as const, slug };
    const handleSetup = () => {
      pendingAuditionIntent.write({
        surface: 'audition',
        slug,
        title: auditionTitle,
      });
      const next = buildApplyJourneyNext(target);
      router.push(
        `/home/artist/profile/setup?next=${encodeURIComponent(next)}`,
      );
    };

    return (
      <Button className="h-11 w-full" onClick={handleSetup}>
        <Trans i18nKey="auditions:singleApplyCta.completeProfileToApply" />
      </Button>
    );
  }

  return (
    <>
      <Button className="h-11 w-full" onClick={() => setDialogOpen(true)}>
        <Trans i18nKey="auditions:singleApplyCta.applyNow" />
      </Button>
      <ApplyDialog
        auditionId={auditionId}
        auditionTitle={auditionTitle}
        applicationStructure={applicationStructure}
        prefillDefaults={prefillDefaults}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}

type ButtonVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'ghost'
  | 'link';

function ctaForExistingApplication(
  status: string,
  allowAcceptedEdits: boolean,
): {
  label: React.ReactNode;
  variant: ButtonVariant;
  icon: React.ReactNode;
} | null {
  // Mirrors Meteor's `applicationCanBeEdited(status)` —
  // open/issues/submitted/archived render a CTA; accepted/rejected
  // are terminal states and show no edit/view button.
  switch (status) {
    case 'open':
      return {
        label: <Trans i18nKey="auditions:singleApplyCta.existing.openLabel" />,
        variant: 'secondary',
        icon: <ArrowRight className="mr-2 h-4 w-4" />,
      };
    case 'issues':
      return {
        label: (
          <Trans i18nKey="auditions:singleApplyCta.existing.issuesLabel" />
        ),
        variant: 'default',
        icon: <ArrowRight className="mr-2 h-4 w-4" />,
      };
    case 'archived':
      return {
        label: (
          <Trans i18nKey="auditions:singleApplyCta.existing.archivedLabel" />
        ),
        variant: 'default',
        icon: <RotateCcw className="mr-2 h-4 w-4" />,
      };
    case 'accepted':
      // Editable after acceptance when the organizer allows it — routes to the
      // (now-editable) application detail page. Frozen → no button.
      return allowAcceptedEdits
        ? {
            label: (
              <Trans i18nKey="auditions:singleApplyCta.existing.acceptedEditLabel" />
            ),
            variant: 'secondary',
            icon: <ArrowRight className="mr-2 h-4 w-4" />,
          }
        : null;
    case 'rejected':
      return null;
    case 'submitted':
    default:
      return {
        label: (
          <Trans i18nKey="auditions:singleApplyCta.existing.submittedLabel" />
        ),
        variant: 'secondary',
        icon: <ArrowRight className="mr-2 h-4 w-4" />,
      };
  }
}
